import Decimal from 'decimal.js';
import { PRICE_DP } from './quote-calc';
import { toAuxPrice, toBasePrice } from './uom';

/**
 * P06 B8 (docs/phases/P06.md §5 mục 9–10): giá đã bán gần nhất, mặt bằng giá, cờ giá cao. Hàm thuần, không đọc DB.
 * Mọi giá so sánh đều là đơn giá SAU chiết khấu dòng, quy về 1 ĐVT chính (1 ĐVT chính = factor × ĐVT phụ).
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
type Num = number | string;

/** DEC-130 A: giá đã bán trong 180 ngày mới làm đơn giá mặc định. */
export const LAST_SOLD_DAYS = 180;
/** DEC-098: bấm vào giá đã bán hiện 5 lần bán gần nhất của khách + mã. */
export const LAST_SOLD_COUNT = 5;
/** DEC-132 A: cửa sổ tính mặt bằng (giá gần nhất của mỗi khách trong 180 ngày). */
export const MARKET_WINDOW_DAYS = 180;
/** DEC-132 A: cần ít nhất 3 khách KHÁC mới gắn cờ. */
export const MIN_OTHER_CUSTOMERS = 3;
/** DEC-131 A: giá ≥ 2 lần mặt bằng (= 200%) là giá cao. */
export const HIGH_PRICE_RATIO = 2;
/** DEC-138 A: giá đã chốt thành giá riêng của khách 180 ngày; B8 dùng cùng hạn cho giá "Đề xuất giảm". */
export const AGREED_PRICE_DAYS = 180;

const DAY_MS = 86_400_000;
const parseDay = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) throw new Error(`Ngày không hợp lệ: ${iso}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
const fmtDay = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Số ngày từ `from` tới `to` (ngày lịch, YYYY-MM-DD). */
export const daysBetween = (from: string, to: string) => Math.round((parseDay(to) - parseDay(from)) / DAY_MS);
/** Cộng ngày cho ngày lịch YYYY-MM-DD. */
export const addCalendarDays = (iso: string, n: number) => fmtDay(parseDay(iso) + n * DAY_MS);
/** Ngày đầu của cửa sổ `days` ngày kết thúc ở `today` (gồm cả hôm nay): 180 ngày → today − 179. */
export const windowStart = (today: string, days: number) => addCalendarDays(today, -(days - 1));
/** Hạn giá riêng khách từ ngày `from`, hiệu lực AGREED_PRICE_DAYS ngày (gồm ngày đầu). */
export const agreedPriceValidTo = (from: string, days = AGREED_PRICE_DAYS) => addCalendarDays(from, days - 1);

/**
 * Đơn giá sau chiết khấu của 1 dòng hóa đơn Asia (chưa VAT), 2 số lẻ.
 * Có tiền hàng + tiền CK + SL → (tiền − CK) ÷ SL (đúng số Asia đã ghi); không có thì đơn giá × (1 − %CK); không có CK thì đơn giá.
 */
export function invoiceNetPrice(l: { unitPrice: Num; quantity: Num; amount?: Num | null; discount?: Num | null; discountRate?: Num | null }): number {
  const qty = new D(l.quantity);
  if (qty.lte(0)) throw new Error('Số lượng phải lớn hơn 0');
  const discount = l.discount === null || l.discount === undefined ? null : new D(l.discount);
  if (l.amount !== null && l.amount !== undefined && discount && !discount.isZero())
    return D.max(0, new D(l.amount).minus(discount)).div(qty).toDecimalPlaces(PRICE_DP).toNumber();
  const price = new D(l.unitPrice);
  if (l.discountRate !== null && l.discountRate !== undefined && !new D(l.discountRate).isZero())
    return D.max(0, price.mul(new D(100).minus(l.discountRate)).div(100)).toDecimalPlaces(PRICE_DP).toNumber();
  return price.toDecimalPlaces(PRICE_DP).toNumber();
}

/** Đơn giá theo ĐVT bán → theo 1 ĐVT chính. */
export const factBasePrice = (netPrice: Num, uomFactor: Num) => toBasePrice(netPrice, uomFactor);

export interface MarketAggregate {
  /** Tổng giá gần nhất (ĐVT chính) của mọi khách có giá trong cửa sổ */
  sumBasePrice: Num;
  customerCount: number;
}

export interface MarketLevel {
  /** Mặt bằng (trung bình giá gần nhất của các khách khác), ĐVT chính, 2 số lẻ */
  avgBasePrice: number;
  /** Số khách khác tính vào mặt bằng */
  customerCount: number;
}

/**
 * DEC-132 A: mặt bằng của 1 mã cho khách X = trung bình giá gần nhất của các khách KHÁC.
 * `ownBasePrice` = giá gần nhất của X trong cùng cửa sổ (null nếu X chưa mua trong cửa sổ).
 * Ít hơn 3 khách khác → null (không đủ dữ liệu, không gắn cờ).
 */
export function marketLevel(agg: MarketAggregate | null | undefined, ownBasePrice: Num | null | undefined): MarketLevel | null {
  if (!agg || agg.customerCount <= 0) return null;
  const hasOwn = ownBasePrice !== null && ownBasePrice !== undefined;
  const others = agg.customerCount - (hasOwn ? 1 : 0);
  if (others < MIN_OTHER_CUSTOMERS) return null;
  const sum = new D(agg.sumBasePrice).minus(hasOwn ? new D(ownBasePrice) : 0);
  const avg = sum.div(others).toDecimalPlaces(PRICE_DP);
  if (avg.lte(0)) return null;
  return { avgBasePrice: avg.toNumber(), customerCount: others };
}

/** Cùng kết quả với marketLevel nhưng tính thẳng từ danh sách giá gần nhất của từng khách (để kiểm và dùng khi không có bảng tổng). */
export function marketLevelFromPrices(latest: { customerId: number; basePrice: Num }[], customerId: number | null): MarketLevel | null {
  const seen = new Set<number>();
  let sum = new D(0);
  let own: Decimal | null = null;
  for (const l of latest) {
    if (seen.has(l.customerId)) continue; // mỗi khách tính 1 lần (dòng đầu = mới nhất)
    seen.add(l.customerId);
    sum = sum.plus(l.basePrice);
    if (l.customerId === customerId) own = new D(l.basePrice);
  }
  return marketLevel({ sumBasePrice: sum.toString(), customerCount: seen.size }, own === null ? null : own.toString());
}

export interface MarketFlag {
  avgBasePrice: number;
  customerCount: number;
  /** Giá ÷ mặt bằng, cắt (không làm tròn lên) 2 số lẻ để 1,999 không hiện thành 2,00 */
  ratio: number;
  /** Giá ≥ 2 × mặt bằng (so sánh chính xác, không qua ratio đã cắt) */
  high: boolean;
}

/** DEC-131 A: tỷ lệ đơn giá sau CK (ĐVT chính) so với mặt bằng; ≥ 2 → giá cao. */
export function marketFlag(netBasePrice: Num, level: MarketLevel | null): MarketFlag | null {
  if (!level) return null;
  const price = new D(netBasePrice);
  const avg = new D(level.avgBasePrice);
  return {
    avgBasePrice: level.avgBasePrice,
    customerCount: level.customerCount,
    ratio: price.div(avg).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber(),
    high: price.gte(avg.mul(HIGH_PRICE_RATIO)),
  };
}

/** Đơn giá sau CK của 1 dòng báo giá, quy về ĐVT chính: đơn giá × (1 − CK%) × hệ số. */
export function quoteLineBasePrice(unitPrice: Num, discountPercent: Num = 0, uomFactor: Num = 1): number {
  const net = new D(unitPrice).mul(new D(100).minus(discountPercent)).div(100).toDecimalPlaces(PRICE_DP);
  return toBasePrice(net.toString(), uomFactor);
}

export interface SoldFact {
  date: string;
  /** Đơn giá sau CK theo ĐVT chính */
  basePrice: Num;
  unit: string;
  uomFactor: Num;
  netPrice: Num;
  quantity: Num;
  invoiceNo?: string | null;
  docNo?: string | null;
}

export interface LastSold {
  /** Giá đã bán quy sang ĐVT được hỏi (mặc định ĐVT chính) */
  price: number;
  /** Giá đã bán theo ĐVT chính */
  basePrice: number;
  uom: string;
  date: string;
  invoiceNo: string | null;
  ageDays: number;
  /** DEC-130 A: trong 180 ngày thì làm đơn giá mặc định */
  usableAsDefault: boolean;
}

/**
 * DEC-130 A: giá đã bán gần nhất (fact mới nhất của đúng khách + mã), quy sang ĐVT `target` bằng hệ số.
 * Không có `target` → ĐVT chính (`baseUnit`). Quá 180 ngày vẫn trả để hiện, nhưng usableAsDefault = false.
 */
export function lastSoldOf(
  fact: SoldFact | null | undefined,
  o: { today: string; baseUnit: string; target?: { uom: string; factor: Num } | null },
): LastSold | null {
  if (!fact) return null;
  const ageDays = daysBetween(fact.date, o.today);
  const target = o.target ?? { uom: o.baseUnit, factor: 1 };
  return {
    price: toAuxPrice(fact.basePrice, target.factor),
    basePrice: new D(fact.basePrice).toDecimalPlaces(PRICE_DP).toNumber(),
    uom: target.uom,
    date: fact.date,
    invoiceNo: fact.invoiceNo ?? fact.docNo ?? null,
    ageDays,
    usableAsDefault: ageDays >= 0 && ageDays < LAST_SOLD_DAYS,
  };
}
